import { useState, useEffect } from 'react';
import { Heart, Eye, EyeOff, ExternalLink, Info, Trophy, History, ChevronRight, User, Settings, Shield, LogOut } from 'lucide-react';
import { useWallet } from '../context/WalletContext';
import { useNavigate } from 'react-router-dom';
import { RequestCard } from '../components/RequestCard';
import { WithdrawModal } from '../components/WithdrawModal';
import { PaymentFlow } from '../components/PaymentFlow';
import type { DonationRequest, Donation } from '../types';
import { useCurrency } from '../hooks/useCurrency';
import { useAuth } from '../context/AuthContext';
import { doc, updateDoc, increment, getDoc, collection, getDocs, setDoc, deleteDoc, query, where, orderBy, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { DeliveryInfo } from '../components/DeliveryForm';
import { NotificationBell } from '../components/NotificationBell';
import { useExtensionPrice } from '../hooks/useExtensionPrice';
import { getUserRanking, get2DRRequests } from '../config/firebase';
import { ReferralShareModal } from '../components/ReferralShareModal';
import { useProfileSettings } from '../context/ProfileSettingsContext';
import { useReferralSettings } from '../hooks/useReferralSettings';
import { useServicesStatus } from '../hooks/useServicesStatus';
import { formatAmount } from '../utils/formatters';
import { ProfilePicture } from '../components/profile/ProfilePicture';
import { navigateToPublicProfile } from '../utils/profileUtils';

interface SocialLink {
  platform: string;
  url: string;
}

interface DeliveryRequest {
  id: string;
  userId: string;
  status: 'pending' | 'completed';
  rewardTitle: string;
  rewardPrice: number;
  createdAt: Date;
  drawDate: Date;
  deliveryInfo?: DeliveryInfo;
  type: 'delivery';
  likes: number;
}

interface TwoDRRequest {
  id: string;
  type: '2dr';
  title: string;
  details: string;
  amount: number;
  hasFixedAmount: boolean;
  donations: Array<{
    amount: number;
    date: Timestamp | string;
    userId: string;
  }>;
  createdAt: Date;
  displayEndDate?: Date;
  displayDuration?: number;
  userId: string;
  isPublic: boolean;
  likes: number;
}

type ActiveRequest = DeliveryRequest | TwoDRRequest;

interface ExtendedDonationRequest extends DonationRequest {
  minDonationAmount: number;
  creatorName: string;
  creatorCity: string;
  creatorCountry: string;
}

interface ProfileData {
  username: string;
  profilePicture: string;
  socialLinks: SocialLink[];
  mobileMoneyAccounts: [];
  externalPhotos: [];
  showExternalPhotos: boolean;
  likes: number;
  description: string;
  amountGiven: number;
}

interface WithdrawInfo {
  phoneNumber: string;
  fullName: string;
}

// Modifier la structure des donations reçues
interface GroupedDonation {
  requestId: string;
  requestTitle: string;
  totalAmount: number;
  donations: Array<{
    amount: number;
    date: Date;
    userId: string;
  }>;
}

export function Profile() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { profileSettings, setProfileSettings: updateProfileSettings } = useProfileSettings();
  const [activeTab, setActiveTab] = useState<'donnes' | 'recus' | 'demandes'>('donnes');
  const [requestsTab, setRequestsTab] = useState<'active' | 'completed' | 'private'>('active');
  const [showDonations, setShowDonations] = useState(false);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [totalLikes, setTotalLikes] = useState(0);
  const [isProfileLiked, setIsProfileLiked] = useState(false);
  const [totalAmountGiven, setTotalAmountGiven] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState<ExtendedDonationRequest | null>(null);
  
  const [donationHistory, setDonationHistory] = useState(() => {
    const saved = localStorage.getItem('donation_history');
    if (!saved) return [];
    const history = JSON.parse(saved);
    return history.filter((donation: any) => donation.userId === user?.id);
  });

  const { balance, updateBalance, requestWithdraw, referralCount, referralEarnings, referralCode } = useWallet();
  const currency = useCurrency();
  const { settings: referralSettings, loading: referralSettingsLoading } = useReferralSettings();
  const { status: servicesStatus } = useServicesStatus();

  const [activeRequests, setActiveRequests] = useState<ActiveRequest[]>([]);
  const [deliveryRequests, setDeliveryRequests] = useState<DeliveryRequest[]>([]);

  const { price: extensionPrice } = useExtensionPrice();

  const [isPaymentFlowOpen, setIsPaymentFlowOpen] = useState(false);

  const fetchDeliveryRequests = async () => {
    try {
      const deliveriesRef = collection(db, 'deliveries');
      const q = query(
        deliveriesRef,
        where('userId', '==', user?.id)
      );
      const snapshot = await getDocs(q);
      const deliveryRequests = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          type: 'delivery' as const,
          userId: data.userId,
          status: data.status,
          rewardTitle: data.rewardTitle,
          rewardPrice: data.rewardPrice,
          createdAt: data.createdAt?.toDate() || new Date(),
          drawDate: data.drawDate?.toDate() || new Date(),
          likes: data.likes || 0
        } as DeliveryRequest;
      });
      setDeliveryRequests(deliveryRequests);
      return deliveryRequests;
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes de livraison:', error);
      return [];
    }
  };

  useEffect(() => {
    const fetchAllRequests = async () => {
      try {
        // Récupérer les demandes de livraison
        const deliveriesRef = collection(db, 'deliveries');
        const q = query(
          deliveriesRef,
          where('userId', '==', user?.id)
        );
        const snapshot = await getDocs(q);
        const deliveryRequests = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            type: 'delivery' as const,
            userId: data.userId,
            status: data.status,
            rewardTitle: data.rewardTitle,
            rewardPrice: data.rewardPrice,
            createdAt: data.createdAt?.toDate() || new Date(),
            drawDate: data.drawDate?.toDate() || new Date(),
            likes: data.likes || 0
          } as DeliveryRequest;
        });

        // Récupérer les demandes 2DR
        const twoDRRequests = JSON.parse(localStorage.getItem('2dr_requests') || '[]')
          .filter((req: any) => req.userId === user?.id)
          .map((req: any) => ({
            ...req,
            type: '2dr' as const,
            donations: (req.donations || [])
              .filter((d: any) => d.userId)
              .map((d: any) => ({
                amount: d.amount,
                date: d.date,
                userId: d.userId
              }))
          })) satisfies TwoDRRequest[];

        // Combiner les deux types de demandes
        setActiveRequests([...deliveryRequests, ...twoDRRequests] as ActiveRequest[]);
      } catch (error) {
        console.error('Erreur lors de la récupération des demandes:', error);
      }
    };

    if (user?.id) {
      fetchAllRequests();
    }
  }, [user?.id]);

  const updateTwoDRRequests = async () => {
    if (!user?.id) return;

    try {
      const [allRequests, deliveryRequests] = await Promise.all([
        get2DRRequests(),
        fetchDeliveryRequests()
      ]);
      
      console.log('Toutes les demandes:', allRequests);
      console.log('Demandes de livraison:', deliveryRequests);

      // Fonction pour vérifier le statut d'une demande
      const checkRequestStatus = (req: any) => {
        const now = new Date();
        const endDate = req.displayEndDate 
          ? new Date(req.displayEndDate)
          : new Date(req.lastExtensionDate || req.createdAt);

        if (req.displayDuration) {
          endDate.setDate(endDate.getDate() + req.displayDuration);
        } else {
          // Par défaut, 30 jours
          endDate.setDate(endDate.getDate() + 30);
        }

        return {
          isActive: now < endDate,
          endDate
        };
      };

      // Filtrer les demandes 2DR de l'utilisateur qui sont actives
      const twoDRRequests = allRequests
        .filter(req => {
          // Vérifier si la demande appartient à l'utilisateur
          const isUserRequest = req.userId === user?.id;
          if (!isUserRequest) return false;
      
      // Pour les demandes sans montant fixe
          if (req.hasFixedAmount === false) {
            // Initialiser les métadonnées du cycle de vie si non définies
            if (!req.requestStatus) {
              req.requestStatus = 'active';
              req.lastExtensionDate = req.lastExtensionDate || (typeof req.createdAt === 'string' ? new Date(req.createdAt) : req.createdAt);
              req.extensionsCount = req.extensionsCount || 0;
            }

            // Vérifier le statut actuel
            const { isActive } = checkRequestStatus(req);
            req.requestStatus = isActive ? 'active' : 'expired';
            return true;
          }
          
          // Pour les demandes avec un montant fixe
          if (req.hasFixedAmount === true) {
            const totalDonations = (req.donations || []).reduce((sum, d) => sum + d.amount, 0);
            return totalDonations < req.amount;
          }

          return true;
        })
        .map(req => ({
          ...req,
          type: '2dr' as const,
          title: req.title || req.details,
          hasFixedAmount: req.hasFixedAmount || false,
          amount: req.amount || 0,
          donations: (req.donations || [])
            .filter(d => d.userId)
            .map(d => ({
              amount: d.amount,
              date: d.date,
              userId: d.userId as string
            })),
          createdAt: new Date(req.createdAt),
          displayEndDate: req.displayEndDate ? new Date(req.displayEndDate) : undefined,
          displayDuration: req.displayDuration || undefined
        })) as TwoDRRequest[];

      // Filtrer les réclamations de lots de l'utilisateur
      const userDeliveryRequests = deliveryRequests.filter(req => 
        req.userId === user.id && req.status === 'pending'
      ).map(req => ({
        ...req,
        type: 'delivery' as const
      }));

      // Combiner les demandes 2DR et les réclamations de lots
      const allActiveRequests = [...twoDRRequests, ...userDeliveryRequests];
      console.log('Toutes les demandes actives:', allActiveRequests);

      setActiveRequests(allActiveRequests);
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes:', error);
    }
  };

  useEffect(() => {
    // Mettre à jour immédiatement au montage
    updateTwoDRRequests();

    // Créer un intervalle pour vérifier régulièrement les changements
    const interval = setInterval(updateTwoDRRequests, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [user]);

  const handleCompleteDelivery = async (deliveryId: string) => {
    // Demander confirmation avant de procéder
    const isConfirmed = window.confirm(
      "Êtes-vous sûr d'avoir reçu cette livraison ? Cette action est irréversible."
    );

    if (!isConfirmed) return;

    try {
      // Mettre à jour le statut dans la collection deliveries
      await updateDoc(doc(db, 'deliveries', deliveryId), {
        status: 'completed',
        completedAt: new Date()
      });

      // Mettre à jour l'état local
      setDeliveryRequests(prev => 
        prev.map(req => 
          req.id === deliveryId 
            ? { ...req, status: 'completed' } 
            : req
        )
      );
    } catch (error) {
      console.error('Erreur lors de la complétion de la livraison:', error);
    }
  };

  const handleLike = (requestId: string) => {
    setActiveRequests(prev => {
      const updatedRequests = prev.map(request => {
        if (request.id === requestId) {
          const isLiked = localStorage.getItem(`liked_${requestId}`) === 'true';
          return {
            ...request,
            likes: isLiked ? request.likes - 1 : request.likes + 1
          };
        }
        return request;
      });
      return updatedRequests;
    });
  };

  // Charger le nombre total de likes et l'état du like
  useEffect(() => {
    if (user?.id) {
      // Charger le nombre total de likes
      const getLikes = async () => {
        const userRef = doc(db, 'users', user.id);
        const userDoc = await getDoc(userRef);
        setTotalLikes(userDoc.data()?.likes || 0);
      };
      
      // Vérifier si l'utilisateur a liké son propre profil
      const checkUserLike = async () => {
        const likeRef = doc(db, 'users', user.id, 'profileLikes', user.id);
        const likeDoc = await getDoc(likeRef);
        setIsProfileLiked(likeDoc.exists());
      };
      
      getLikes();
      checkUserLike();
    }
  }, [user?.id]);

  const handleProfileLike = async () => {
    if (!user?.id) return;
    
    try {
      const likeRef = doc(db, 'users', user.id, 'profileLikes', user.id);
      const likeDoc = await getDoc(likeRef);
      const hasLiked = likeDoc.exists();
      const userRef = doc(db, 'users', user.id);

      if (!hasLiked) {
        await updateDoc(userRef, {
          likes: increment(1)
        });
        await setDoc(likeRef, {
          userId: user.id,
          timestamp: new Date()
        });
        setTotalLikes(prev => prev + 1);
        setIsProfileLiked(true);
      } else if (likeDoc.data()?.userId === user.id) {
        await updateDoc(userRef, {
          likes: increment(-1)
        });
        await deleteDoc(likeRef);
        setTotalLikes(prev => prev - 1);
        setIsProfileLiked(false);
      }

    } catch (error) {
      console.error('Erreur lors de la mise à jour du like:', error);
    }
  };

  // Fonction pour ouvrir le flux de paiement
  const handleDepositClick = () => {
    if (!servicesStatus?.deposits.active) {
      alert(servicesStatus?.deposits.message || "Les recharges sont temporairement indisponibles");
      return;
    }
    setIsPaymentFlowOpen(true);
  };

  // Fonction pour gérer le succès du paiement
  const handlePaymentSuccess = () => {
    alert('Recharge effectuée avec succès');
    setIsPaymentFlowOpen(false);
  };

  // Fonction pour gérer l'annulation du paiement
  const handlePaymentCancel = () => {
    setIsPaymentFlowOpen(false);
  };

  // Fonction pour ouvrir la modale de retrait
  const handleWithdrawClick = () => {
    if (!servicesStatus?.withdrawals.active) {
      alert(servicesStatus?.withdrawals.message || "Les retraits sont temporairement indisponibles");
      return;
    }
    setIsWithdrawModalOpen(true);
  };

  // Fonction pour gérer le retrait
  const handleWithdraw = async (amount: number, withdrawInfo: WithdrawInfo) => {
    try {
      const success = await requestWithdraw(amount, withdrawInfo);
      if (success) {
        alert('Demande de retrait envoyée avec succès');
        setIsWithdrawModalOpen(false);
      } else {
        alert('Erreur lors de la demande de retrait');
      }
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Erreur lors du retrait');
    }
  };

  const handleViewPublicProfile = () => {
    if (user?.id) {
      navigateToPublicProfile(user.id, navigate);
    }
  };

  const handleViewSettings = () => {
    navigate('/profil/settings');
  };

  const handleViewTransactions = () => {
    navigate('/transactions');
  };

  // Ajouter cette fonction pour mettre à jour l'historique après un don
  const updateDonationHistory = (newDonation: Donation) => {
    setDonationHistory((prev: Donation[]) => [...prev, newDonation]);
    localStorage.setItem('donation_history', JSON.stringify([...donationHistory, newDonation]));
  };

  // Écouter les changements de l'historique des dons
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'donation_history') {
        const newDonation = JSON.parse(e.newValue || '[]').pop();
        if (newDonation) {
          updateDonationHistory(newDonation);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    if (!user?.id) return;

    const loadUserProfile = async () => {
      try {
        const userRef = doc(db, 'users', user.id);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // Récupérer la photo de profil depuis localStorage
          const savedProfilePicture = localStorage.getItem(`user_${user.id}_profilePicture`);
          
          setProfileData({
            username: userData.username || '',
            // Utiliser la photo depuis localStorage en priorité
            profilePicture: savedProfilePicture || '',
            socialLinks: userData.socialLinks || [],
            mobileMoneyAccounts: userData.mobileMoneyAccounts || [],
            externalPhotos: userData.externalPhotos || [],
            showExternalPhotos: userData.showExternalPhotos || false,
            likes: userData.likes || 0,
            description: userData.description || '',
            amountGiven: userData.amountGiven || 0
          });
        }

        // Calculer le total des donations
        const donationsQuery = query(
          collection(db, 'recentDonations'),
          where('userId', '==', user.id),
          where('is_public', '==', true)
        );

        const donationsSnapshot = await getDocs(donationsQuery);
        let totalAmount = 0;
        donationsSnapshot.forEach((doc) => {
          const donation = doc.data();
          totalAmount += donation.amount;
        });

        // Mettre à jour le profil avec le total
        await updateDoc(userRef, {
          amountGiven: totalAmount
        });
      } catch (error) {
        console.error('Erreur lors du chargement du profil:', error);
      }
    };

    loadUserProfile();
  }, [user?.id]);

  const [sentDonations, setSentDonations] = useState<Donation[]>([]);
  const [receivedDonations, setReceivedDonations] = useState<GroupedDonation[]>([]);
  const [donationNotifications, setDonationNotifications] = useState<any[]>([]);

  // Écouter les donations envoyées et reçues
  useEffect(() => {
    if (!user?.id) return;

    // Écouter les donations envoyées
    const sentQuery = query(
      collection(db, 'recentDonations'),
      where('userId', '==', user.id),
      where('is_public', '==', true)
    );

    const unsubscribeSent = onSnapshot(sentQuery, (snapshot) => {
      const donations: Donation[] = [];
      let totalAmount = 0;
      
      snapshot.forEach((doc) => {
        const donation = { id: doc.id, ...doc.data() } as Donation;
        donations.push(donation);
        totalAmount += donation.amount;
      });

      setSentDonations(donations);
      setProfileData(prev => ({
        ...prev,
        amountGiven: totalAmount
      }));
    });

    // Récupérer d'abord toutes les demandes de l'utilisateur
    const requestsQuery = query(
      collection(db, '2dr_requests'),
      where('userId', '==', user.id)
    );

    const unsubscribeReceived = onSnapshot(requestsQuery, async (snapshot) => {
      const requests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as TwoDRRequest));

      const groupedDonations = requests.reduce<GroupedDonation[]>((acc, request) => {
        if (!request.donations || request.donations.length === 0) return acc;

        const totalAmount = request.donations.reduce((sum, donation) => sum + donation.amount, 0);

        if (totalAmount > 0) {
          acc.push({
            requestId: request.id,
            requestTitle: request.title || request.details,
            totalAmount,
            donations: request.donations
              .filter(d => d.userId)
              .map(d => ({
                amount: d.amount,
                date: (d.date as unknown as Timestamp)?.toDate?.() || new Date(d.date as string),
                userId: d.userId as string
              }))
          });
        }

        return acc;
      }, []);

      setReceivedDonations(groupedDonations);
    });

    return () => {
      unsubscribeSent();
      unsubscribeReceived();
    };
  }, [user?.id]);

  // Écouter les notifications de donations
  useEffect(() => {
    if (!user?.id) return;

    // Écouter les notifications de donations envoyées
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', user.id),
      where('type', '==', 'donation_sent'),
      where('status', '==', 'unread'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeNotifications = onSnapshot(notificationsQuery, (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDonationNotifications(notifications);
    });

    return () => {
      unsubscribeNotifications();
    };
  }, [user?.id]);

  const handleDonationClick = (requestId: string) => {
    navigate(`/2dr/request/${requestId}`);
  };

  const [profileData, setProfileData] = useState<ProfileData>({
    username: '',
    profilePicture: '',
    socialLinks: [],
    mobileMoneyAccounts: [],
    externalPhotos: [],
    showExternalPhotos: false,
    likes: 0,
    description: '',
    amountGiven: 0
  });

  const [userRanking, setUserRanking] = useState<number | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    
    const fetchRanking = async () => {
      const ranking = await getUserRanking(user.id);
      setUserRanking(ranking);
    };

    fetchRanking();
  }, [user?.id, totalAmountGiven]);

  // Ajouter un state pour gérer l'état de la copie
  const [copyStatus, setCopyStatus] = useState('');

  const shareReferralLink = () => {
    const referralLink = `${window.location.origin}/parrainage/${user?.referralCode}`;
    navigator.clipboard.writeText(referralLink);
    
    // Afficher "Bravo!" pendant la copie
    setCopyStatus('Bravo!');
    
    // Réinitialiser après 2 secondes
    setTimeout(() => {
      setCopyStatus('');
    }, 2000);
  };

  const renderRequests = (status: 'active' | 'completed' | 'private') => {
    // Filtrer d'abord les demandes 2DR
    const filteredTwoDRRequests = activeRequests.filter(request => {
      if (!('type' in request) || request.type !== '2dr') return false;
      
      if (request.userId !== user?.id) return false;
      
      if (status === 'private') return !request.isPublic;
      
      if (request.hasFixedAmount) {
        const totalDonations = request.donations.reduce((sum, d) => sum + d.amount, 0);
        return status === 'completed' ? 
          totalDonations >= request.amount : 
          totalDonations < request.amount;
      }
      
      return status === 'active';
    });

    // Filtrer les réclamations de lots (ne pas les inclure dans 'private')
    const filteredDeliveryRequests = status !== 'private' ? deliveryRequests.filter(request => {
      return status === 'completed' ? 
        request.status === 'completed' : 
        request.status === 'pending';
    }) : [];

    return (
      <div className="space-y-2">
        {/* Afficher les réclamations de lots */}
        {filteredDeliveryRequests.map((request) => (
          <div
            key={`delivery-${request.id}`}
            className="bg-white/5 p-4 rounded-xl cursor-pointer hover:bg-white/10 transition-colors"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="text-white font-medium">{request.rewardTitle}</p>
                <div className="flex flex-col">
                  <p className="text-gray-400 text-sm">
                    Réclamation
                  </p>
                  <p className="text-gray-400 text-sm">
                    {new Date(request.drawDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="text-yellow-400">
                  Lot
                </div>
                {status === 'active' && (
                  <button
                    onClick={() => handleCompleteDelivery(request.id)}
                    className="px-3 py-1 text-xs bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                  >
                    Terminer
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Afficher les demandes 2DR */}
        {filteredTwoDRRequests.map((request) => {
          if (request.type !== '2dr') return null;
          
          const totalDonations = request.donations.reduce((sum: number, d: any) => sum + d.amount, 0);
          const daysRemaining = (request as any).daysRemaining;
          const endDate = (request as any).endDate;
          
          return (
            <div
              key={request.id}
              onClick={() => navigate(`/2dr/request/${request.id}`)}
              className="bg-white/5 p-4 rounded-xl cursor-pointer hover:bg-white/10 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-white font-medium">{request.title}</p>
                  {request.hasFixedAmount ? (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-green-400">{totalDonations.toLocaleString()} FCFA</span>
                      <span className="text-gray-400">sur {request.amount.toLocaleString()} FCFA</span>
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <div className="text-gray-400 text-sm">
                        Sans montant
                      </div>
                      {status === 'active' && daysRemaining !== undefined && (
                        <div className={`text-sm ${
                          daysRemaining <= 5 ? 'text-yellow-400' : 'text-gray-400'
                        }`}>
                          {daysRemaining > 0 ? (
                            <>
                              <span>Expire dans {daysRemaining} jour{daysRemaining > 1 ? 's' : ''}</span>
                              {daysRemaining <= 5 && (
                                <div className="mt-1 text-xs bg-yellow-400/20 text-yellow-400 px-2 py-1 rounded-lg inline-block">
                                  Expiration imminente
                                </div>
                              )}
                            </>
                          ) : (
                            <span>Expiré le {endDate?.toLocaleDateString()}</span>
                          )}
                        </div>
                      )}
                      {status === 'completed' && request.type === '2dr' && (
                        <button
                          onClick={() => handleExtendDisplay(request.id)}
                          className="px-3 py-1 text-xs bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                        >
                          Prolonger (30j)
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="text-green-400">
                  Pub
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Ajouter l'état pour le filtre
  // const [requestsFilter, setRequestsFilter] = useState<'ongoing' | 'completed' | 'private'>('ongoing');

  // Ajouter l'écoute en temps réel
  useEffect(() => {
    if (!user?.id) return;

    const deliveriesQuery = query(
      collection(db, 'deliveries'),
      where('userId', '==', user.id)
    );

    const unsubscribe = onSnapshot(deliveriesQuery, (snapshot) => {
      const requests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        drawDate: doc.data().drawDate?.toDate()
      })) as DeliveryRequest[];
      
      console.log('Toutes les réclamations:', requests);
      setDeliveryRequests(requests);
    });

    return () => unsubscribe();
  }, [user?.id]);

  useEffect(() => {
    console.log('Delivery Requests:', deliveryRequests);
    console.log('Active Requests:', activeRequests);
  }, [deliveryRequests, activeRequests]);

  const renderReceivedDonations = () => (
    <div className="space-y-4">
      {receivedDonations.length > 0 ? (
        receivedDonations.map((groupedDonation) => (
          <div
            key={groupedDonation.requestId}
            onClick={() => navigate(`/2dr/request/${groupedDonation.requestId}`)}
            className="bg-black/40 p-4 rounded-xl cursor-pointer hover:bg-black/50 transition-all border border-white/5 hover:border-white/10"
          >
            <h3 className="text-white font-semibold mb-2">
              {groupedDonation.requestTitle}
            </h3>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">
                {groupedDonation.donations.length} donation{groupedDonation.donations.length > 1 ? 's' : ''}
              </span>
              <span className="text-green-400 font-medium">
                {groupedDonation.totalAmount.toLocaleString()} {currency}
              </span>
            </div>
          </div>
        ))
      ) : (
        <p className="text-center text-gray-400">Aucune donation reçue</p>
      )}
    </div>
  );

  const loadProfileSettings = async () => {
    if (!user?.id) return;
    const userDoc = await getDoc(doc(db, 'users', user.id));
    if (userDoc.exists()) {
      const data = userDoc.data();
      updateProfileSettings({
        username: data.username || '',
        description: data.description || '',
        profilePicture: data.profilePicture || '',
        country: data.country || '',
        city: data.city || '',
        socialLinks: data.socialLinks || [],
        mobileMoneyAccounts: data.mobileMoneyAccounts || [],
        externalPhotos: data.externalPhotos || [],
        showExternalPhotos: data.showExternalPhotos ?? true,
        likes: data.likes || 0,
        referralCode: data.referralCode || '',
      });
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    const unsubscribe = onSnapshot(doc(db, 'users', user.id), (doc) => {
      if (doc.exists()) {
        loadProfileSettings();
      }
    });
    return () => unsubscribe();
  }, [user?.id]);

  const handleExtendDisplay = async (requestId: string) => {
    if (!user?.id || balance < extensionPrice) {
      alert(`Solde insuffisant. Il vous faut ${extensionPrice} FCFA pour prolonger l'affichage.`);
      return;
    }

    try {
      const requestRef = doc(db, '2dr_requests', requestId);
      const requestDoc = await getDoc(requestRef);
      
      if (!requestDoc.exists()) return;

      // Déduire le coût
      const success = await updateBalance(-extensionPrice);
      if (!success) {
        alert("Erreur lors du paiement");
        return;
      }

      // Prolonger l'affichage
      await updateDoc(requestRef, {
        displayDuration: increment(30),
        displayEndDate: new Date(Date.now() + (30 * 24 * 60 * 60 * 1000))
      });

      alert("Affichage prolongé de 30 jours !");
    } catch (error) {
      console.error('Erreur lors de la prolongation:', error);
      alert("Une erreur est survenue");
    }
  };

  // Ajouter cet useEffect pour écouter les donations
  useEffect(() => {
    if (!user?.id) return;

    // Écouter les donations envoyées
    const sentQuery = query(
      collection(db, 'donations'),
      where('userId', '==', user.id),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(sentQuery, (snapshot) => {
      const total = snapshot.docs.reduce((sum, doc) => {
        const donation = doc.data();
        return sum + (donation.amount || 0);
      }, 0);
      setTotalAmountGiven(total);
    });

    return () => unsubscribe();
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    const userRef = doc(db, 'users', user.id);
    const unsubscribe = onSnapshot(userRef, (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        setTotalAmountGiven(userData.amountGiven || 0);
      }
    });

    return () => unsubscribe();
  }, [user?.id]);

  return (
    <div className="max-w-2xl mx-auto">
      {selectedRequest ? (
        <RequestCard 
          id={selectedRequest.id}
          showBackButton={true}
          onBack={() => setSelectedRequest(null)}
          minDonationAmount={selectedRequest.minDonationAmount || 100}
          name={profileSettings?.username || 'Anonyme'}
          profilePicture={profileSettings?.profilePicture}
          description={selectedRequest.details}
          goal={selectedRequest.amount}
          uniqueDonorsCount={selectedRequest.donations.filter(d => !d.isAnonymous).length}
          hasDonated={selectedRequest.donations.some(d => d.userId === 'user123')}
          onLike={() => handleLike(selectedRequest.id)}
          collected={selectedRequest.donations.reduce((sum, d) => sum + d.amount, 0)}
          creatorName={selectedRequest.creatorName}
          creatorCity={selectedRequest.creatorCity}
          creatorCountry={selectedRequest.creatorCountry}
          creatorId={selectedRequest.userId || ''}
          donations={selectedRequest.donations}
          likes={selectedRequest.likes}
        />
      ) : (
        <div>
          <div className="relative mb-4">
            <div className="text-center mb-4">
              <div className="flex flex-col items-center mb-4 bg-black/20 p-4 pt-6 rounded-xl">
                <div className="mb-3">
                  <ProfilePicture 
                    src={profileData?.profilePicture}
                    username={profileData?.username}
                  />
                </div>
                <div className="text-center pt-2">
                  <div className="relative">
                    <h1 className="text-xl font-bold text-white flex items-center justify-center gap-1">
                      {profileSettings.username}
                      <button
                        onClick={() => navigate(`/username-history/${user.id}`)}
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        <Info className="w-4 h-4" />
                      </button>
                    </h1>
                    <div className="flex items-center justify-center gap-3 mt-3">
                      <p className="text-green-400">
                        {formatAmount(totalAmountGiven)} {currency}
                        <span className="text-gray-400 text-sm ml-1">Donations</span>
                      </p>
                      <button
                        onClick={handleProfileLike}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-xl ${
                          isProfileLiked 
                            ? 'bg-pink-500/20 text-pink-400 hover:bg-pink-500/30' 
                            : 'bg-white/5 hover:bg-white/10 text-gray-300'
                        }`}
                      >
                        <Heart className={`w-4 h-4 ${isProfileLiked ? 'fill-current' : ''}`} />
                        <span className="text-sm">{totalLikes}</span>
                      </button>
                      {userRanking && (
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/5 text-yellow-400">
                          <Trophy className="w-4 h-4" />
                          <span className="text-sm">#{userRanking}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {profileSettings.description && (
                <p className="text-gray-400 mb-2 text-center whitespace-pre-line">
                  {profileSettings.description.split('\n').map((line, i) => {
                    const followKeywords = ['suivant', 'suivez', 'suivre'];
                    const hasFollowWord = followKeywords.some(keyword => 
                      line.toLowerCase().includes(keyword)
                    );

                    if (hasFollowWord && profileSettings.socialLinks?.length > 0) {
                      return (
                        <span key={i}>
                          {line}{' '}
                          {profileSettings.socialLinks.map((link: SocialLink, index: number) => (
                            <a
                              key={index}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-400 hover:text-green-300 transition-colors inline-flex items-center gap-1"
                            >
                              {link.platform}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ))}
                        </span>
                      );
                    }
                    return <span key={i}>{line}{'\n'}</span>;
                  })}
                </p>
              )}
            </div>
          </div>

          {/* Onglets Donnés/Reçus/Demandes */}
          <div className="border-b border-white/10 mb-2">
            <div className="flex border-b border-white/10">
              <div
                className="px-4 hover:text-white transition-colors cursor-pointer text-gray-400 flex items-center"
                onClick={() => setShowDonations(!showDonations)}
              >
                {showDonations ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </div>
              <button
                className={`flex-1 py-3 text-center transition-colors ${
                  activeTab === 'donnes'
                  ? 'text-green-400'
                  : 'text-gray-400 hover:text-white'
                }`}
                onClick={() => setActiveTab('donnes')}
              >
                Donnés
              </button>
              <button
                className={`flex-1 py-3 text-center transition-colors ${
                  activeTab === 'recus'
                  ? 'text-green-400'
                  : 'text-gray-400 hover:text-white'
                }`}
                onClick={() => setActiveTab('recus')}
              >
                Reçus
              </button>
              <button
                className={`flex-1 py-3 text-center transition-colors ${
                  activeTab === 'demandes'
                  ? 'text-green-400'
                  : 'text-gray-400 hover:text-white'
                }`}
                onClick={() => setActiveTab('demandes')}
              >
                Pub&Lots
              </button>
            </div>

            <div className="p-4 space-y-4">
              {showDonations ? (
                activeTab === 'donnes' ? (
                  <div className="space-y-4">
                    {donationNotifications.length > 0 && (
                      <div className="space-y-2">
                        {donationNotifications.map((notification) => (
                          <div
                            key={notification.id}
                            onClick={() => handleDonationClick(notification.requestId)}
                            className="bg-white/5 p-4 rounded-xl cursor-pointer hover:bg-white/10 transition-colors"
                          >
                            <h3 className="text-white font-bold">{notification.requestTitle}</h3>
                            <p className="text-green-400">
                              {notification.amount.toLocaleString()} {currency}
                            </p>
                            <p className="text-gray-400 text-sm">
                              {new Date(notification.date.toDate()).toLocaleDateString('fr-FR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                    {sentDonations.length > 0 ? (
                      sentDonations.map((donation) => (
                        <div
                          key={donation.id}
                          onClick={() => handleDonationClick(donation.requestId)}
                          className="border border-white/10 p-4 rounded-xl hover:border-white/20 transition-colors cursor-pointer"
                        >
                          <h3 className="text-white font-bold">{donation.requestTitle}</h3>
                          <p className="text-green-400">
                            {donation.amount.toLocaleString()} {currency}
                          </p>
                          <p className="text-gray-400 text-sm">
                            {typeof donation.date === 'string' 
                              ? new Date(donation.date).toLocaleDateString('fr-FR', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })
                              : 'Date non disponible'
                            }
                          </p>
                        </div>
                      ))
                    ) : (
                      donationNotifications.length === 0 && (
                        <p className="text-center text-gray-400">Aucune donation</p>
                      )
                    )}
                  </div>
                ) : activeTab === 'recus' ? (
                  renderReceivedDonations()
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center w-full max-w-md mx-auto">
                      <div className="inline-flex rounded-xl bg-black/20 p-1 w-full justify-between">
                        <button
                          onClick={() => setRequestsTab('active')}
                          className={`px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors rounded-lg ${
                            requestsTab === 'active'
                              ? 'bg-white/10 text-white'
                              : 'text-gray-400 hover:text-white'
                          }`}
                        >
                          En cours
                        </button>
                        <button
                          onClick={() => setRequestsTab('completed')}
                          className={`px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors rounded-lg ${
                            requestsTab === 'completed'
                              ? 'bg-white/10 text-white'
                              : 'text-gray-400 hover:text-white'
                          }`}
                        >
                          Terminées
                        </button>
                        <button
                          onClick={() => setRequestsTab('private')}
                          className={`px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors rounded-lg ${
                            requestsTab === 'private'
                              ? 'bg-white/10 text-white'
                              : 'text-gray-400 hover:text-white'
                          }`}
                        >
                          Privées
                        </button>
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {renderRequests(requestsTab)}
                    </div>
                  </div>
                )
              ) : (
                <p className="text-gray-400 text-center">Cliquez sur l'icône de l'œil pour voir</p>
              )}
            </div>
          </div>

          {/* Portefeuille */}
          <div className="mt-4 bg-white/5 backdrop-blur-lg rounded-3xl p-6 border border-white/10 mb-6">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h2 className="text-xl font-bold text-white">Portefeuille</h2>
                <p className="text-gray-400 text-sm">solde actuel</p>
                <p className="text-2xl font-bold text-green-400 truncate">
                  {formatAmount(balance)} {currency}
                </p>
              </div>
              <NotificationBell />
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleDepositClick}
                className="flex-1 bg-green-500 text-white px-4 py-2 rounded-lg"
              >
                Recharger
              </button>
              <button
                onClick={handleWithdrawClick}
                className="flex-1 bg-white/10 text-white px-4 py-2 rounded-lg"
              >
                Retirer
              </button>
            </div>
          </div>

          {/* Section Parrainage */}
          <div className="bg-white/5 p-4 rounded-xl space-y-4">
            <h3 className="text-lg font-bold text-white">Parrainage</h3>
            
            {referralSettingsLoading ? (
              <p className="text-gray-400">Chargement...</p>
            ) : referralSettings.isActive ? (
              <>
                <p className="text-white">
                  Gagnez {referralSettings.reward} FCFA/pers parrainée!
                </p>
                
                <div className="flex items-center gap-2">
                  <input 
                    type="text" 
                    value={`${window.location.origin}/parrainage/${user?.referralCode}`}
                    readOnly
                    className="bg-white/10 text-white p-2 rounded-lg flex-1"
                  />
                  <button
                    onClick={shareReferralLink}
                    className={`px-2 py-2 rounded-lg transition-all ${
                      copyStatus 
                        ? 'bg-red-500 text-white' 
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}
                  >
                    {copyStatus || 'Copier'}
                  </button>
                </div>

                <div>
                  <p className="text-gray-400 mb-2">Vos statistiques :</p>
                  <p className="text-white">Parrainages : {referralCount}/{referralSettings.maxReferrals}</p>
                  <p className="text-white">Gains : {referralEarnings} FCFA</p>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-yellow-400">
                  Le programme de parrainage est temporairement suspendu.
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  Revenez plus tard pour parrainer vos amis !
                </p>
              </div>
            )}
          </div>

          {/* Nouvelles sections */}
          <div className="mt-6 space-y-4">
            {/* Historique de transaction */}
            <div 
              className="bg-white/5 p-4 rounded-xl hover:bg-white/10 transition-all cursor-pointer flex items-center justify-between"
              onClick={handleViewTransactions}
            >
              <div className="flex items-center gap-3">
                <div className="bg-blue-500/20 p-2 rounded-full">
                  <History className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="text-white font-medium">Historique de transactions</h3>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>

            {/* Voir mon profil public */}
            <div 
              className="bg-white/5 p-4 rounded-xl hover:bg-white/10 transition-all cursor-pointer flex items-center justify-between"
              onClick={handleViewPublicProfile}
            >
              <div className="flex items-center gap-3">
                <div className="bg-purple-500/20 p-2 rounded-full">
                  <User className="w-5 h-5 text-purple-400" />
                </div>
                <h3 className="text-white font-medium">Voir mon profil public</h3>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>

            {/* Paramètres du profil */}
            <div 
              className="bg-white/5 p-4 rounded-xl hover:bg-white/10 transition-all cursor-pointer flex items-center justify-between"
              onClick={handleViewSettings}
            >
              <div className="flex items-center gap-3">
                <div className="bg-amber-500/20 p-2 rounded-full">
                  <Settings className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="text-white font-medium">Paramètres du profil</h3>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>

            {/* Politique de confidentialité et termes d'utilisation */}
            <div 
              className="bg-white/5 p-4 rounded-xl hover:bg-white/10 transition-all cursor-pointer flex items-center justify-between"
              onClick={() => navigate('/legal')}
            >
              <div className="flex items-center gap-3">
                <div className="bg-teal-500/20 p-2 rounded-full">
                  <Shield className="w-5 h-5 text-teal-400" />
                </div>
                <h3 className="text-white font-medium">Politique de confidentialité et CGU</h3>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>

            {/* Déconnexion */}
            <div 
              className="bg-red-500/10 p-4 rounded-xl hover:bg-red-500/20 transition-all cursor-pointer flex items-center justify-between"
              onClick={signOut}
            >
              <div className="flex items-center gap-3">
                <div className="bg-red-500/20 p-2 rounded-full">
                  <LogOut className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="text-red-400 font-medium">Déconnexion</h3>
              </div>
              <ChevronRight className="w-5 h-5 text-red-400" />
            </div>
          </div>
        </div>
      )}

      {/* Ajouter les modales */}
      <PaymentFlow
        onSuccess={handlePaymentSuccess}
        onCancel={handlePaymentCancel}
        currency={currency}
        isOpen={isPaymentFlowOpen}
      />

      <WithdrawModal
        isOpen={isWithdrawModalOpen}
        onClose={() => setIsWithdrawModalOpen(false)}
        onWithdraw={handleWithdraw}
        balance={balance}
      />

      <ReferralShareModal
        isOpen={isReferralModalOpen}
        onClose={() => setIsReferralModalOpen(false)}
        referralCode={referralCode}
      />
    </div>
  );
}